import { supabase } from '@/lib/supabase';
import { getOrSetCache } from '@/lib/coreCache';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 58. DASHBOARD CORE
// Tableau de bord central, SIMPLE et volontairement limité.
// Il n'affiche QUE les informations pertinentes pour l'utilisateur :
//   Aujourd'hui  → rendez-vous · ventes · commandes · paiements · tâches · notifications
//   Puis         → activité · clients · revenus · actions à faire
// Zifek Core n'est PAS un dashboard métier : les dashboards spécialisés
// appartiennent aux modules.
// ─────────────────────────────────────────────────────────────

export interface CoreDashboardToday {
  appointments: number;
  orders: number;
  sales: number;
  payments: number;
  tasks: number;
  notifications: number;
}

export interface CoreDashboardTotals {
  customers: number;
  revenue: number;
  activity: number;
  openActions: number;
}

export interface CoreDashboardTodo {
  icon: string;
  label: string;
  detail: string;
  severity: 'info' | 'warning' | 'success';
  href?: string;
}

export interface CoreDashboardData {
  currency: string;
  today: CoreDashboardToday;
  totals: CoreDashboardTotals;
  todos: CoreDashboardTodo[];
  recentActivity: { id: number; title: string; description: string | null; event_type: string | null; amount: number | null; currency: string | null; occurred_at: string | null }[];
  upcoming: { id: number; title: string; start_at: string; event_type: string | null; location: string | null }[];
  loadedAt: string;
}

function startOfToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function endOfToday(): string {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

/**
 * Agrège le tableau de bord central. Chaque bloc est best-effort :
 * un module non activé ne doit pas casser le dashboard.
 */
async function loadCoreDashboardData(orgId: number, commerceId: number | null, currency = 'USD'): Promise<CoreDashboardData> {
  const t0 = startOfToday();
  const t1 = endOfToday();

  const today: CoreDashboardToday = { appointments: 0, orders: 0, sales: 0, payments: 0, tasks: 0, notifications: 0 };
  const totals: CoreDashboardTotals = { customers: 0, revenue: 0, activity: 0, openActions: 0 };
  const todos: CoreDashboardTodo[] = [];
  let recentActivity: CoreDashboardData['recentActivity'] = [];
  let upcoming: CoreDashboardData['upcoming'] = [];

  // ── Rendez-vous du jour ──
  try {
    const { count } = await supabase
      .from('booking_appointments')
      .select('id', { count: 'exact', head: true })
      .eq('owner', commerceId)
      .gte('date', t0)
      .lte('date', t1);
    today.appointments = count ?? 0;
  } catch { /* module absent */ }

  // ── Commandes / ventes du jour ──
  try {
    const { data } = await supabase
      .from('commande')
      .select('totalttc, statut, created_at')
      .eq('idvendeur', commerceId)
      .gte('created_at', t0)
      .lte('created_at', t1);
    const rows = (data || []) as { totalttc: string | null; statut: string | null }[];
    today.orders = rows.length;
    today.sales = rows.length;
    today.payments = rows.filter((r) => (r.statut || '').toLowerCase().includes('pay')).length;
  } catch { /* module absent */ }

  // ── Tâches du jour (événements calendrier type task) ──
  try {
    const { count } = await supabase
      .from('core_calendar_events')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', orgId)
      .eq('event_type', 'task')
      .gte('start_at', t0)
      .lte('start_at', t1);
    today.tasks = count ?? 0;
  } catch { /* module absent */ }

  // ── Notifications non lues ──
  try {
    const { count } = await supabase
      .from('core_notifications')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', orgId)
      .eq('is_read', false);
    today.notifications = count ?? 0;
  } catch { /* module absent */ }

  // ── Clients ──
  try {
    const { count } = await supabase
      .from('core_customers')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', orgId);
    totals.customers = count ?? 0;
  } catch { /* module absent */ }

  // ── Revenus (factures payées) ──
  try {
    const { data } = await supabase
      .from('finance_invoices')
      .select('total, status, merchant_id')
      .eq('merchant_id', commerceId);
    const rows = (data || []) as { total: string | null; status: string | null }[];
    totals.revenue = rows
      .filter((r) => (r.status || '').toLowerCase() === 'paid')
      .reduce((acc, r) => acc + (parseFloat(String(r.total ?? '0')) || 0), 0);
  } catch { /* module absent */ }

  // ── Activité récente (Timeline centrale) ──
  try {
    const { data } = await supabase
      .from('core_activity_timeline')
      .select('id, title, description, event_type, amount, currency, occurred_at')
      .eq('organization_id', orgId)
      .order('occurred_at', { ascending: false })
      .limit(8);
    recentActivity = (data || []) as CoreDashboardData['recentActivity'];
    totals.activity = recentActivity.length;
  } catch { /* module absent */ }

  // ── Prochains événements / rendez-vous ──
  try {
    const { data } = await supabase
      .from('core_calendar_events')
      .select('id, title, start_at, event_type, location')
      .eq('organization_id', orgId)
      .gte('start_at', new Date().toISOString())
      .order('start_at', { ascending: true })
      .limit(5);
    upcoming = (data || []) as CoreDashboardData['upcoming'];
  } catch { /* module absent */ }

  // ── Actions à faire ──
  try {
    const { data } = await supabase
      .from('core_privacy_requests')
      .select('id, request_type, status')
      .eq('organization_id', orgId)
      .in('status', ['pending', 'in_progress']);
    const pendingPrivacy = (data || []).length;
    totals.openActions = pendingPrivacy;
    if (pendingPrivacy > 0) {
      todos.push({ icon: 'ri-shield-user-line', label: `${pendingPrivacy} demande(s) de confidentialité`, detail: 'À traiter dans Confidentialité.', severity: 'warning', href: '/dashboard/socle' });
    }
  } catch { /* module absent */ }

  // Signalements d'avis ouverts
  try {
    const { count } = await supabase
      .from('core_review_reports')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', orgId)
      .eq('status', 'open');
    if ((count ?? 0) > 0) {
      todos.push({ icon: 'ri-flag-line', label: `${count} avis signalé(s)`, detail: 'À modérer dans la rubrique Avis.', severity: 'warning', href: '/dashboard/socle' });
    }
  } catch { /* module absent */ }

  if (todos.length === 0) {
    todos.push({ icon: 'ri-checkbox-circle-line', label: 'Tout est à jour', detail: 'Aucune action en attente.', severity: 'success' });
  }

  return {
    currency,
    today,
    totals,
    todos,
    recentActivity,
    upcoming,
    loadedAt: new Date().toISOString(),
  };
}

export async function fetchCoreDashboard(orgId: number, commerceId: number | null, currency = 'USD'): Promise<CoreDashboardData> {
  return getOrSetCache<CoreDashboardData>({
    organizationId: orgId,
    key: `dashboard:summary:${commerceId ?? 0}:${currency}`,
    scope: 'dashboard',
    tags: ['dashboard'],
    ttlSeconds: 300,
    loader: () => loadCoreDashboardData(orgId, commerceId, currency),
  });
}